import { EErrorCode } from 'config/errors-codes';
import type { IBrand, IMarket } from 'data/schema';
import { parseDealerId } from './dealerIdFormat';

export interface IDealerIdValidationContext {
  /** Undefined = no dealer list to check membership against (membership is skipped). */
  dealerIds?: readonly string[];
  brand: IBrand;
  market: IMarket;
}

export type TDealerIdErrorReason = 'invalid-format' | 'not-owned' | 'brand-market-mismatch';

export interface IDealerIdError {
  reason: TDealerIdErrorReason;
  code: EErrorCode;
  dealerId: string;
  message: string;
}

export type TDealerIdValidation =
  | { ok: true; dealerId: string; membershipVerified: boolean }
  | { ok: false; membershipVerified: boolean; error: IDealerIdError };

function dealerIdError(
  reason: TDealerIdErrorReason,
  code: EErrorCode,
  dealerId: string,
  message: string
): IDealerIdError {
  return { reason, code, dealerId, message };
}

/**
 * Pure, side-effect-free Dealer ID validation: format, then membership (only when a list is
 * supplied), then brand/market. Never imports storage, state or utils/auth/user.
 */
export function validateDealerId(dealerId: string, context: IDealerIdValidationContext): TDealerIdValidation {
  const parsed = parseDealerId(dealerId);

  if (!parsed) {
    return {
      ok: false,
      membershipVerified: false,
      error: dealerIdError(
        'invalid-format',
        EErrorCode.DEALER_ID_INVALID_FORMAT,
        dealerId,
        `Dealer ID "${dealerId}" is not in a valid format.`
      ),
    };
  }

  const { dealerIds } = context;
  const membershipVerified = dealerIds !== undefined && dealerIds.includes(dealerId);

  if (dealerIds !== undefined && !membershipVerified) {
    return {
      ok: false,
      membershipVerified: false,
      error: dealerIdError(
        'not-owned',
        EErrorCode.DEALER_NOT_OWNED,
        dealerId,
        `Dealer ID "${dealerId}" is not assigned to this user.`
      ),
    };
  }

  if (parsed.brand !== context.brand || parsed.market !== context.market) {
    return {
      ok: false,
      membershipVerified,
      error: dealerIdError(
        'brand-market-mismatch',
        EErrorCode.DEALER_NO_MATCHING_BRAND_MARKET,
        dealerId,
        `Dealer ID "${dealerId}" does not match the required brand and market.`
      ),
    };
  }

  return { ok: true, dealerId, membershipVerified };
}
